import * as React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { BarsIcon, TimesIcon, BoltIcon } from '@patternfly/react-icons';
import {
  FaTachometerAlt,
  FaDesktop,
  FaBullhorn,
  FaUsers,
  FaCar,
  FaCalendarAlt,
  FaCreditCard,
  FaCog,
  FaShieldAlt
} from 'react-icons/fa';
import { routes } from '@app/routes';
import { getUserInfo } from '@app/utils/utils';
import { UsernameDropdown } from '@app/AppLayout/UsernameDropdown';
import { useWindowSize, isMobile, isTablet } from '@app/utils/useWindowSize';
import { SIDEBAR_GRADIENT } from '@app/components/designTokens';

interface IAppLayout {
  children: React.ReactNode;
}

const routeIcons: { [key: string]: React.ReactNode } = {
  Overview: <FaTachometerAlt />,
  Screens: <FaDesktop />,
  Campaigns: <FaBullhorn />,
  Moderation: <FaShieldAlt />,
  Users: <FaUsers />,
  Drivers: <FaCar />,
  Schedules: <FaCalendarAlt />,
  Billing: <FaCreditCard />,
  Settings: <FaCog />
};

const AppLayout: React.FunctionComponent<IAppLayout> = ({ children }) => {
  const location = useLocation();
  const [userInfo, setUserInfo] = useState<any>();
  const [isNavOpen, setIsNavOpen] = useState(true);
  const [hoveredNav, setHoveredNav] = useState<string | null>(null);
  const { width } = useWindowSize();
  const mobile = isMobile(width);
  const tablet = isTablet(width);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    getUserInfo()
      .then(info => setUserInfo(info))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (tablet) {
      setIsNavOpen(false);
    } else if (!mobile) {
      setIsNavOpen(true);
    }
  }, [tablet, mobile]);

  const displayName = userInfo?.displayName || localStorage.getItem('displayName') || '';

  const onNavToggle = () => {
    if (mobile) {
      setMobileMenuOpen(!mobileMenuOpen);
    } else {
      setIsNavOpen(!isNavOpen);
    }
  };

  const sidebarWidth = isNavOpen ? 240 : 76;

  const styles: { [k: string]: React.CSSProperties } = {
    layout: { display: 'flex', minHeight: '100vh', height: '100vh', overflow: 'hidden', position: 'relative' },
    sidebarOverlay: {
      position: 'fixed',
      inset: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      zIndex: 99,
      display: mobile && mobileMenuOpen ? 'block' : 'none'
    },
    sidebar: {
      width: mobile ? '280px' : sidebarWidth + 'px',
      minWidth: mobile ? '280px' : sidebarWidth + 'px',
      background: SIDEBAR_GRADIENT,
      display: 'flex',
      flexDirection: 'column',
      transition: 'all 0.3s ease',
      position: mobile ? 'fixed' : 'relative',
      top: mobile ? 0 : undefined,
      left: mobile ? (mobileMenuOpen ? '0' : '-280px') : undefined,
      bottom: mobile ? 0 : undefined,
      zIndex: 100,
      boxShadow: mobile && mobileMenuOpen ? '4px 0 20px rgba(0,0,0,0.3)' : '4px 0 20px rgba(0,0,0,0.1)',
      flexShrink: 0,
      height: mobile ? '100vh' : undefined
    },
    logoSection: {
      padding: isNavOpen ? '20px' : '20px 16px',
      borderBottom: '1px solid rgba(255,255,255,0.1)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: isNavOpen ? 'flex-start' : 'center',
      gap: '12px',
      minHeight: '70px'
    },
    logoIcon: {
      width: '36px',
      height: '36px',
      minWidth: '36px',
      borderRadius: '10px',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#fbbf24',
      fontSize: '18px',
      flexShrink: 0,
      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)'
    },
    logoText: {
      color: '#fff',
      fontSize: '17px',
      fontWeight: 700,
      letterSpacing: '-0.5px',
      opacity: isNavOpen ? 1 : 0,
      width: isNavOpen ? 'auto' : 0,
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      transition: 'opacity 0.2s ease, width 0.3s ease'
    },
    navSection: { flex: 1, padding: isNavOpen ? '20px 12px' : '20px 8px', overflowY: 'auto', overflowX: 'hidden' },
    navLabel: {
      color: 'rgba(255,255,255,0.4)',
      fontSize: '11px',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '1px',
      padding: '0 8px',
      marginBottom: '12px',
      opacity: isNavOpen ? 1 : 0,
      height: isNavOpen ? 'auto' : 0,
      overflow: 'hidden'
    },
    navList: { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '4px' },
    navLink: {
      display: 'flex',
      alignItems: 'center',
      gap: '14px',
      padding: isNavOpen ? '14px 16px' : '14px 0',
      color: 'rgba(255,255,255,0.7)',
      textDecoration: 'none',
      borderRadius: '12px',
      transition: 'all 0.2s ease',
      justifyContent: isNavOpen ? 'flex-start' : 'center',
      position: 'relative',
      width: '100%'
    },
    navLinkHover: { background: 'rgba(255,255,255,0.08)', color: '#fff' },
    navLinkActive: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: '#fff',
      boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)'
    },
    navIcon: {
      fontSize: '18px',
      width: '24px',
      height: '24px',
      minWidth: '24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0
    },
    navText: {
      fontSize: '14px',
      fontWeight: 500,
      whiteSpace: 'nowrap',
      display: isNavOpen ? 'block' : 'none'
    },
    tooltip: {
      position: 'absolute',
      left: '100%',
      marginLeft: '10px',
      background: '#1f2937',
      color: '#fff',
      padding: '8px 12px',
      borderRadius: '8px',
      fontSize: '13px',
      fontWeight: 500,
      whiteSpace: 'nowrap',
      zIndex: 1000,
      boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
      opacity: 0,
      pointerEvents: 'none',
      transition: 'opacity 0.2s ease'
    },
    tooltipVisible: { opacity: 1 },
    sidebarFooter: {
      padding: isNavOpen ? '16px' : '16px 8px',
      borderTop: '1px solid rgba(255,255,255,0.1)',
      display: isNavOpen ? 'block' : 'none'
    },
    versionBadge: {
      background: 'rgba(102, 126, 234, 0.2)',
      color: '#a5b4fc',
      padding: '6px 12px',
      borderRadius: '20px',
      fontSize: '11px',
      fontWeight: 600,
      textAlign: 'center'
    },
    mainArea: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#f8fafc' },
    header: {
      background: '#fff',
      height: mobile ? '56px' : '70px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: mobile ? '0 12px' : '0 24px',
      borderBottom: '1px solid #e5e7eb',
      boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
    },
    headerLeft: { display: 'flex', alignItems: 'center', gap: '16px' },
    menuButton: {
      width: '40px',
      height: '40px',
      borderRadius: '10px',
      border: 'none',
      background: '#f3f4f6',
      color: '#4b5563',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '18px'
    },
    headerTitle: { fontSize: '18px', fontWeight: 600, color: '#1f2937' },
    headerRight: { display: 'flex', alignItems: 'center', gap: '16px' },
    content: { flex: 1, overflow: 'auto' }
  };

  const currentRoute =
    routes.find(r => r.path === location.pathname) ||
    routes.find(r => r.label && r.path !== '/' && location.pathname.startsWith(r.path));
  const pageTitle = currentRoute?.label || 'Overview';

  return (
    <div style={styles.layout}>
      <div style={styles.sidebarOverlay} onClick={() => setMobileMenuOpen(false)} />

      <aside style={styles.sidebar}>
        <div style={styles.logoSection}>
          <div style={styles.logoIcon}>
            <BoltIcon className="logo-bolt-pulse" />
          </div>
          <span style={styles.logoText}>AdVolt Admin</span>
        </div>

        <nav style={styles.navSection}>
          <div style={styles.navLabel}>Operations</div>
          <ul style={styles.navList}>
            {routes.map((route, idx) => {
              if (!route.label) return null;
              const isActive =
                route.path === '/'
                  ? location.pathname === '/'
                  : location.pathname === route.path || location.pathname.startsWith(route.path + '/');
              const isHovered = hoveredNav === route.label;
              const icon = routeIcons[route.label];

              return (
                <li key={`${route.label}-${idx}`}>
                  <NavLink
                    end
                    to={route.path}
                    style={{
                      ...styles.navLink,
                      ...(isActive ? styles.navLinkActive : {}),
                      ...(!isActive && isHovered ? styles.navLinkHover : {})
                    }}
                    onMouseEnter={() => setHoveredNav(route.label || null)}
                    onMouseLeave={() => setHoveredNav(null)}
                  >
                    <span style={styles.navIcon}>{icon}</span>
                    <span style={styles.navText}>{route.label}</span>
                    {!isNavOpen && (
                      <span style={{ ...styles.tooltip, ...(isHovered ? styles.tooltipVisible : {}) }}>
                        {route.label}
                      </span>
                    )}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>

        <div style={styles.sidebarFooter}>
          <div style={styles.versionBadge}>v{process.env.APP_VERSION}</div>
        </div>
      </aside>

      <div style={styles.mainArea}>
        <header style={styles.header}>
          <div style={styles.headerLeft}>
            <button style={styles.menuButton} onClick={onNavToggle} aria-label="Toggle navigation">
              {isNavOpen ? <TimesIcon /> : <BarsIcon />}
            </button>
            <span style={styles.headerTitle}>{pageTitle}</span>
          </div>
          <div style={styles.headerRight}>
            <UsernameDropdown username={displayName} />
          </div>
        </header>

        <main style={styles.content} id="primary-app-container">
          {children}
        </main>
      </div>
    </div>
  );
};

export { AppLayout };
